import React, { useState, useEffect } from "react";
import { Table, Button, message, Dropdown, Modal, Descriptions } from "antd";
import { DeleteOutlined, ReloadOutlined, MoreOutlined, EyeOutlined } from "@ant-design/icons";
import { apiClient } from "../../utils/apiClient";
import { studentQueriesApi } from "../../config/api";
import "./PopupEnquiries.scss"; // Using same styles since layout is identical

const StudentQueries = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const response = await apiClient(studentQueriesApi);
      const result = await response.json();
      if (response.ok) {
        setQueries(Array.isArray(result) ? result : (result.data || []));
      } else {
        throw new Error(result.message || "Failed to fetch queries");
      }
    } catch (error) {
      console.error("Error fetching queries:", error);
      message.error("Failed to load queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, []);

  const handleDelete = async (id) => {
    try {
      const response = await apiClient(`${studentQueriesApi}/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error("Failed to delete query");
      }
      message.success("Query deleted successfully");
      fetchQueries();
    } catch (error) {
      console.error("Error deleting query:", error);
      message.error("Failed to delete query");
    }
  };

  const showDeleteConfirm = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this query?",
      content: `This will permanently delete the query from ${record.name || "this student"}.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => handleDelete(record._id),
    });
  };

  const handleView = (record) => {
    setSelectedQuery(record);
    setIsViewModalOpen(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Query / Message",
      dataIndex: "queryText",
      key: "queryText",
      ellipsis: true,
      render: (text, record) => text || record.message || "-",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (text ? new Date(text).toLocaleString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center",
      render: (_, record) => {
        const actionItems = [
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            onClick: () => handleView(record),
          },
          {
            type: "divider",
          },
          {
            key: "delete",
            label: "Delete",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => showDeleteConfirm(record),
          },
        ];

        return (
          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="popup-enquiries-container">
      <div className="header-actions">
        <h2>Student Queries</h2>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={fetchQueries}
          loading={loading}
        >
          Refresh
        </Button>
      </div>
      <div className="table-container">
        <Table
          columns={columns}
          dataSource={queries}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </div>

      <Modal
        title="Student Query Details"
        open={isViewModalOpen}
        onCancel={() => {
          setIsViewModalOpen(false);
          setSelectedQuery(null);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedQuery && (
          <Descriptions column={1} bordered size="small" style={{ marginTop: 16 }}>
            <Descriptions.Item label="Name">{selectedQuery.name || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{selectedQuery.mobile || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedQuery.email || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Subject">{selectedQuery.subject || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Date">
              {selectedQuery.createdAt ? new Date(selectedQuery.createdAt).toLocaleString() : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Query / Message">
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  background: "#f9f9f9",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {selectedQuery.queryText || selectedQuery.message || "N/A"}
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default StudentQueries;
